import {handleActions} from 'redux-actions';
import {Record, OrderedMap} from 'immutable';

import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import PlatformHelper from 'web-app/helpers/platform-helper';

import * as Actions from './actions';

const State = Record({
    isVisible: false,
    institutionId: undefined,
    billPayerId: undefined,
    inferredChildId: undefined,
    noItemsToInvoice: false,
    currentPreview: null,
    showDetails: false,
    previews: OrderedMap(),
    saving: false,
    loading: false,
    latestPreviewId: null,
});

const Preview = Record({
    id: null,
    invoice: null,
    details: null,
    warnings: null,
    error: null,
});

const reducer = handleActions(
    {
        [Actions.show.type]: (state, {payload}) =>
            new State({
                isVisible: true,
                billPayerId: payload.billPayerId,
                inferredChildId: payload.inferredChildId,
                institutionId: payload.institutionId,
            }),
        [Actions.hide.type]: () => new State(),
        [Actions.storeInvoicePreviewId.type]: (state, {payload}) => {
            return state.merge({
                latestPreviewId: payload.requestId,
                previews: state.previews.set(payload.requestId, null),
            });
        },
        [Actions.showDetails.type]: (state, {payload}) =>
            state.merge({
                showDetails: payload.show,
            }),
        [Actions.previewLoaded.type]: (state, {payload}) => {
            const previewIds = state.previews.keySeq();

            const stateWithRequiredUpdates = state.merge({
                loading: false,
            });

            if (previewIds.indexOf(payload.requestId) > previewIds.indexOf(state.currentPreview)) {
                return stateWithRequiredUpdates.merge({
                    currentPreview: payload.requestId,
                });
            }
            return stateWithRequiredUpdates;
        },
        [Actions.previewInvoice.type]: state =>
            state.merge({
                loading: true,
            }),
        [Actions.previewInvoiceFailed.type]: (state, {payload}) => {
            if (state.latestPreviewId !== payload.requestId) {
                return state;
            }

            const message = payload.error;

            return state.merge({
                loading: false,
                noItemsToInvoice: false,
                previews: message
                    ? state.previews.set(
                          state.latestPreviewId,
                          new Preview({
                              id: payload.requestId,
                              warnings: [],
                              error: message,
                          }),
                      )
                    : state.previews,
            });
        },
        [Actions.previewInvoiceSuccess.type]: (state, {payload}) => {
            if (state.latestPreviewId !== payload.requestId) {
                return state;
            }

            let currentPreview;
            let loadingInvoicePdf = false;

            // If we're already showing details we don't need to wait for the pdf iframe to load so we switch previews
            // immediately. Same if we have previously not been able to show an invoice
            if (state.showDetails || state.noItemsToInvoice || !PlatformHelper.hasPdfInIframeSupport()) {
                currentPreview = payload.requestId;
            }
            // Otherwise use the latestPreviewId which represents the latest change in the form
            else {
                currentPreview = state.latestPreviewId;
                loadingInvoicePdf = true;
            }

            return state.merge({
                loading: loadingInvoicePdf,
                noItemsToInvoice: false,
                currentPreview,
                previews: state.previews.set(
                    state.latestPreviewId,
                    new Preview({
                        id: payload.requestId,
                        invoice: payload.result.invoice,
                        details: payload.result.details,
                        warnings: payload.result.warnings,
                    }),
                ),
            });
        },
        [Actions.previewInvoiceAborted.type]: (state, {payload}) => {
            const previewIds = state.previews.keySeq();
            if (previewIds.indexOf(payload.requestId) > previewIds.indexOf(state.currentPreview)) {
                return state.merge({
                    noItemsToInvoice: true,
                    currentPreview: payload.requestId,
                    loading: false,
                    previews: state.previews.set(
                        payload.requestId,
                        new Preview({
                            id: payload.requestId,
                            warnings: payload.warnings || [],
                        }),
                    ),
                });
            }
            return state;
        },
        [AccountItemActions.createInvoice.type]: state =>
            state.merge({
                saving: true,
            }),
        [AccountItemActions.createInvoiceSuccess.type]: () => new State(),
        [AccountItemActions.createInvoiceAborted.type]: state =>
            state.merge({
                saving: false,
            }),
        [AccountItemActions.createInvoiceFailed.type]: state =>
            state.merge({
                saving: false,
            }),
    },
    new State(),
);

export default reducer;
